import type { FC } from 'react';
import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import { LinkButton } from '@atlaskit/button/new';

import { ADMIN_TRASHED_SPACES } from '@confluence/named-routes';

const i18n = defineMessages({
	trashMessage: {
		id: 'space-directory.trash.message',
		defaultMessage:
			'Trashed spaces can be permanently deleted or restored from Confluence settings.',
		description:
			'Message to inform the user that trashed spaces can be permanently deleted or restored from Confluence settings',
	},
	goToSettings: {
		id: 'space-directory.trash.go-to-settings',
		defaultMessage: 'Go to settings',
		description: 'Button link to go to the admin settings',
	},
});

//svg illustration
const TrashIllustration = () => (
	<svg
		width="120"
		height="116"
		viewBox="0 0 120 116"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
	>
		<g clipPath="url(#clip0_116_4647)">
			<rect width="120" height="115.256" fill="white" fillOpacity="0.01" />
			<path
				d="M74.7882 19.0238C68.689 19.0238 63.9451 18.1525 59.9274 16.7487C47.4385 12.3921 46.1315 6.34127 33.449 2.27511C27.3981 0.338846 22.0734 0 18.5397 0C14.7156 0 10.359 0.871319 6.87374 1.74264C2.9044 2.71077 0 3.77572 0 3.77572L0.43566 6.92215L13.5539 105.962H92.2146C92.2146 105.962 104.655 7.3094 103.058 9.19726C102.38 9.97176 101.509 10.7947 100.395 11.5692C95.5063 15.0061 86.4058 19.0238 74.7882 19.0238Z"
				fill="url(#paint0_linear_116_4647)"
			/>
			<path
				d="M116.999 98.9431L91.2949 93.1343L92.4567 88.1484L120 85.5829L116.999 98.9431Z"
				fill="url(#paint1_linear_116_4647)"
			/>
			<path d="M108.431 103.735L117.047 99.0884L104.946 94.1509L108.431 103.735Z" fill="#FFE380" />
			<path
				d="M41.2424 18.2493L44.5341 14.1832L50.3429 6.97058L59.6854 13.8927L61.3796 15.1513L69.8992 21.3958L41.2424 18.2493Z"
				fill="url(#paint2_linear_116_4647)"
			/>
			<path
				d="M47.8257 18.3461L44.5341 14.1831L42.8398 16.2646L45.8895 18.3461H47.8257Z"
				fill="#FFE380"
			/>
			<path
				d="M22.509 24.3002L35.3368 13.6023H41.2424L48.7454 19.9436L39.887 24.3002H22.509Z"
				fill="#F4F5F7"
			/>
			<path d="M35.3369 13.6023L38.4833 18.0557L41.2425 13.6023" fill="#EBECF0" />
			<path d="M38.3865 16.3131L41.2425 13.6023H35.3369L38.3865 16.3131Z" fill="white" />
			<path
				d="M111.461 90.344L85.7732 84.5481L82.758 97.9115L108.446 103.707L111.461 90.344Z"
				fill="#FFF0B2"
			/>
			<path
				d="M103.058 22.7512C100.734 23.4772 98.0235 23.9129 95.0223 24.0581C94.4898 24.0581 93.9089 24.1065 93.328 24.1065C92.9892 24.1065 92.6503 24.1065 92.3599 24.1065H92.3115C92.021 24.1065 91.6822 24.1065 91.3918 24.0581C91.1013 24.0581 90.8109 24.0097 90.5204 24.0097H90.472C90.1816 24.0097 89.8912 23.9613 89.6491 23.9129H89.6007C89.3103 23.8645 89.0198 23.8645 88.7778 23.8161H88.7294C87.2772 23.6225 85.9218 23.3804 84.6632 23.0416C84.6632 23.0416 84.6632 23.0416 84.6148 23.0416C84.3728 22.9932 84.1308 22.8964 83.9371 22.848C83.8887 22.848 83.8403 22.7996 83.7919 22.7996C83.5983 22.7512 83.4047 22.7027 83.211 22.6059C83.1626 22.6059 83.1142 22.5575 83.0658 22.5575C82.9206 22.5091 82.727 22.4607 82.5818 22.3639C82.5333 22.3639 82.4365 22.3155 82.3881 22.3155C82.2429 22.2671 82.0977 22.2187 81.9525 22.1219C81.9041 22.0735 81.8072 22.0735 81.7588 22.0251C81.6136 21.9766 81.4684 21.8798 81.3232 21.8314C81.2748 21.783 81.2264 21.783 81.1296 21.7346C80.9843 21.6862 80.8391 21.5894 80.6939 21.541C80.6455 21.541 80.5971 21.4926 80.5487 21.4926C80.4035 21.3958 80.2098 21.299 80.0646 21.2505C80.0646 21.2505 80.0162 21.2505 80.0162 21.2021C77.9347 19.992 76.6761 18.4914 76.6761 16.894C76.6761 12.9246 84.1308 9.68136 93.3764 9.68136C97.6362 9.68136 101.557 10.3591 104.51 11.5208L105.333 5.03433C101.315 3.3885 94.8286 1.25861 86.6479 1.21021C83.1626 1.21021 77.8379 1.54905 71.7871 3.48532C59.1045 7.55147 57.7492 13.6023 45.2603 17.9105C41.2425 19.3143 36.4987 20.1856 30.3994 20.1856C18.7818 20.1856 9.72979 16.2163 4.84073 12.731C1.16182 10.1654 0.484131 7.30944 0.484131 7.30944L6.53496 55.3288H98.9916L103.058 22.7512Z"
				fill="url(#paint3_linear_116_4647)"
			/>
			<path
				d="M95.3126 85.1473L98.9915 55.3772H6.53491L10.2622 85.1473H95.3126Z"
				fill="url(#paint4_linear_116_4647)"
			/>
			<path
				d="M95.3126 85.1473L98.9915 55.3772H6.53491L10.2622 85.1473H95.3126Z"
				fill="url(#paint5_linear_116_4647)"
			/>
			<path
				d="M13.0698 107.172H92.5535L95.3126 85.1472H10.2622L13.0698 107.172Z"
				fill="url(#paint6_linear_116_4647)"
			/>
			<path
				opacity="0.46"
				d="M13.0698 107.172H92.5535L95.3126 85.1472H10.2622L13.0698 107.172Z"
				fill="url(#paint7_linear_116_4647)"
			/>
			<path d="M13.0698 107.172H92.5535L95.3126 85.1472H10.2622L13.0698 107.172Z" fill="#0065FF" />
			<path
				d="M83.6951 22.7511C83.5015 22.7027 83.3079 22.6543 83.1143 22.5575C83.3079 22.6543 83.5015 22.7027 83.6951 22.7511Z"
				fill="url(#paint8_linear_116_4647)"
			/>
			<path
				d="M83.6951 22.7511C83.5015 22.7027 83.3079 22.6543 83.1143 22.5575C83.3079 22.6543 83.5015 22.7027 83.6951 22.7511Z"
				fill="url(#paint9_linear_116_4647)"
			/>
			<path
				d="M84.518 22.9932C84.276 22.9448 84.034 22.848 83.8403 22.7996C84.034 22.848 84.276 22.9448 84.518 22.9932Z"
				fill="url(#paint10_linear_116_4647)"
			/>
			<path
				d="M84.518 22.9932C84.276 22.9448 84.034 22.848 83.8403 22.7996C84.034 22.848 84.276 22.9448 84.518 22.9932Z"
				fill="url(#paint11_linear_116_4647)"
			/>
			<path
				d="M88.6326 23.7677C87.1804 23.574 85.825 23.332 84.5664 22.9932C85.7766 23.332 87.1804 23.6224 88.6326 23.7677Z"
				fill="url(#paint12_linear_116_4647)"
			/>
			<path
				d="M88.6326 23.7677C87.1804 23.574 85.825 23.332 84.5664 22.9932C85.7766 23.332 87.1804 23.6224 88.6326 23.7677Z"
				fill="url(#paint13_linear_116_4647)"
			/>
			<path
				d="M95.0221 24.0581C94.4896 24.0581 93.9088 24.1065 93.3279 24.1065C93.9088 24.0581 94.4412 24.0581 95.0221 24.0581Z"
				fill="url(#paint14_linear_116_4647)"
			/>
			<path
				d="M95.0221 24.0581C94.4896 24.0581 93.9088 24.1065 93.3279 24.1065C93.9088 24.0581 94.4412 24.0581 95.0221 24.0581Z"
				fill="url(#paint15_linear_116_4647)"
			/>
			<path
				d="M90.3752 23.9613C90.0847 23.9613 89.7943 23.9129 89.5522 23.8645C89.8427 23.9129 90.1331 23.9613 90.3752 23.9613Z"
				fill="url(#paint16_linear_116_4647)"
			/>
			<path
				d="M90.3752 23.9613C90.0847 23.9613 89.7943 23.9129 89.5522 23.8645C89.8427 23.9129 90.1331 23.9613 90.3752 23.9613Z"
				fill="url(#paint17_linear_116_4647)"
			/>
			<path
				d="M91.3432 24.0097C91.0528 24.0097 90.7624 23.9613 90.4719 23.9613C90.7624 24.0097 91.0528 24.0097 91.3432 24.0097Z"
				fill="url(#paint18_linear_116_4647)"
			/>
			<path
				d="M91.3432 24.0097C91.0528 24.0097 90.7624 23.9613 90.4719 23.9613C90.7624 24.0097 91.0528 24.0097 91.3432 24.0097Z"
				fill="url(#paint19_linear_116_4647)"
			/>
			<path
				d="M89.5038 23.8645C89.2134 23.8161 88.9229 23.8161 88.6809 23.7677C88.9229 23.8161 89.2134 23.8645 89.5038 23.8645Z"
				fill="url(#paint20_linear_116_4647)"
			/>
			<path
				d="M89.5038 23.8645C89.2134 23.8161 88.9229 23.8161 88.6809 23.7677C88.9229 23.8161 89.2134 23.8645 89.5038 23.8645Z"
				fill="url(#paint21_linear_116_4647)"
			/>
			<path
				d="M92.263 24.0581C91.9726 24.0581 91.6337 24.0581 91.3433 24.0096C91.6337 24.0581 91.9726 24.0581 92.263 24.0581Z"
				fill="url(#paint22_linear_116_4647)"
			/>
			<path
				d="M92.263 24.0581C91.9726 24.0581 91.6337 24.0581 91.3433 24.0096C91.6337 24.0581 91.9726 24.0581 92.263 24.0581Z"
				fill="url(#paint23_linear_116_4647)"
			/>
			<path
				d="M81.662 22.025C81.5168 21.9766 81.3715 21.8798 81.2263 21.8314C81.3715 21.9282 81.5168 21.9766 81.662 22.025Z"
				fill="url(#paint24_linear_116_4647)"
			/>
			<path
				d="M81.662 22.025C81.5168 21.9766 81.3715 21.8798 81.2263 21.8314C81.3715 21.9282 81.5168 21.9766 81.662 22.025Z"
				fill="url(#paint25_linear_116_4647)"
			/>
			<path
				d="M82.9689 22.5091C82.8237 22.4607 82.6301 22.4122 82.4849 22.3154C82.6301 22.4122 82.8237 22.4607 82.9689 22.5091Z"
				fill="url(#paint26_linear_116_4647)"
			/>
			<path
				d="M82.9689 22.5091C82.8237 22.4607 82.6301 22.4122 82.4849 22.3154C82.6301 22.4122 82.8237 22.4607 82.9689 22.5091Z"
				fill="url(#paint27_linear_116_4647)"
			/>
			<path
				d="M81.0812 21.783C80.9359 21.7346 80.7907 21.6378 80.6455 21.5894C80.7907 21.6378 80.9359 21.6862 81.0812 21.783Z"
				fill="url(#paint28_linear_116_4647)"
			/>
			<path
				d="M81.0812 21.783C80.9359 21.7346 80.7907 21.6378 80.6455 21.5894C80.7907 21.6378 80.9359 21.6862 81.0812 21.783Z"
				fill="url(#paint29_linear_116_4647)"
			/>
			<path
				d="M80.5002 21.4925C80.355 21.3957 80.1613 21.2989 80.0161 21.2505C80.1613 21.2989 80.3066 21.3957 80.5002 21.4925Z"
				fill="url(#paint30_linear_116_4647)"
			/>
			<path
				d="M80.5002 21.4925C80.355 21.3957 80.1613 21.2989 80.0161 21.2505C80.1613 21.2989 80.3066 21.3957 80.5002 21.4925Z"
				fill="url(#paint31_linear_116_4647)"
			/>
			<path
				d="M82.2911 22.267C82.1459 22.2186 82.0007 22.1702 81.8555 22.0734C82.0007 22.1702 82.1459 22.2186 82.2911 22.267Z"
				fill="url(#paint32_linear_116_4647)"
			/>
			<path
				d="M82.2911 22.267C82.1459 22.2186 82.0007 22.1702 81.8555 22.0734C82.0007 22.1702 82.1459 22.2186 82.2911 22.267Z"
				fill="url(#paint33_linear_116_4647)"
			/>
			<path
				d="M76.6277 16.8455C76.6277 18.4913 77.8863 19.992 79.9677 21.1537C79.7741 21.0569 79.6289 20.9601 79.4353 20.8633L83.9371 16.8455L87.4708 20.234L91.1981 17.378L94.9738 24.0097C97.975 23.8645 100.686 23.4288 103.009 22.7027L104.413 11.4724C101.46 10.359 97.5877 9.63293 93.2796 9.63293C84.0823 9.63293 76.6277 12.8762 76.6277 16.8455Z"
				fill="url(#paint34_linear_116_4647)"
			/>
			<path
				d="M76.6277 16.8455C76.6277 18.4913 77.8863 19.992 79.9677 21.1537C79.7741 21.0569 79.6289 20.9601 79.4353 20.8633L83.9371 16.8455L87.4708 20.234L91.1981 17.378L94.9738 24.0097C97.975 23.8645 100.686 23.4288 103.009 22.7027L104.413 11.4724C101.46 10.359 97.5877 9.63293 93.2796 9.63293C84.0823 9.63293 76.6277 12.8762 76.6277 16.8455Z"
				fill="url(#paint35_linear_116_4647)"
			/>
			<path
				d="M87.4707 20.2824L91.3432 24.0097C91.6337 24.0097 91.9725 24.0581 92.263 24.0581H92.3114C92.6502 24.0581 92.9406 24.0581 93.2795 24.0581C93.8604 24.0581 94.3928 24.0581 94.9737 24.0097L91.198 17.4264L87.4707 20.2824Z"
				fill="#FFE380"
			/>
			<path
				d="M87.4708 20.2824L83.9371 16.8939L79.4353 20.9117C79.5805 21.0085 79.7741 21.1053 79.9678 21.2021C79.9678 21.2021 80.0162 21.2021 80.0162 21.2505C80.1614 21.3473 80.355 21.4441 80.5002 21.4925C80.5487 21.4925 80.5971 21.541 80.6455 21.541C80.7907 21.5894 80.9359 21.6862 81.0811 21.7346C81.1295 21.783 81.1779 21.783 81.2747 21.8314C81.42 21.8798 81.5652 21.9766 81.7104 22.025C81.7588 22.0734 81.8556 22.0734 81.904 22.1218C82.0493 22.1702 82.1945 22.2186 82.3397 22.3155C82.3881 22.3155 82.4849 22.3639 82.5333 22.3639C82.6785 22.4123 82.8722 22.4607 83.0174 22.5575C83.0658 22.5575 83.1142 22.6059 83.1626 22.6059C83.3562 22.6543 83.5499 22.7027 83.7435 22.7995C83.7919 22.7995 83.8403 22.8479 83.8887 22.8479C84.1307 22.8963 84.3244 22.9932 84.5664 23.0416C84.5664 23.0416 84.5664 23.0416 84.6148 23.0416C85.8734 23.3804 87.2288 23.6224 88.681 23.8161H88.7294C89.0198 23.8645 89.2618 23.8645 89.5523 23.9129H89.6007C89.8911 23.9613 90.1816 23.9613 90.4236 24.0097H90.472C90.7624 24.0097 91.0529 24.0581 91.3433 24.0581L87.4708 20.2824Z"
				fill="#FFF0B2"
			/>
			<path
				d="M89.7944 100.541L77.0634 91.6821L63.9937 109.738L71.7387 115.256L89.7944 100.541Z"
				fill="#DFE1E5"
			/>
			<path d="M102.816 108.77L100.734 95.7968L93.3279 100.541L102.816 108.77Z" fill="#DFE1E5" />
			<path
				d="M71.7385 115.256L93.8603 108.77H102.816L88.1968 91.2949L71.7385 115.256Z"
				fill="#F4F5F7"
			/>
		</g>
		<defs>
			<linearGradient
				id="paint0_linear_116_4647"
				x1="0"
				y1="52.9822"
				x2="103.184"
				y2="52.9822"
				gradientUnits="userSpaceOnUse"
			>
				<stop offset="0.0334535" stopColor="#0049B0" />
				<stop offset="0.6292" stopColor="#005ADF" />
				<stop offset="1" stopColor="#0065FF" />
			</linearGradient>
			<linearGradient
				id="paint1_linear_116_4647"
				x1="102.162"
				y1="94.0215"
				x2="124.619"
				y2="89.7362"
				gradientUnits="userSpaceOnUse"
			>
				<stop offset="0.00132211" stopColor="#FFE380" />
				<stop offset="0.1843" stopColor="#FFE485" />
				<stop offset="0.3901" stopColor="#FFE792" />
				<stop offset="0.6069" stopColor="#FFECA9" />
				<stop offset="0.83" stopColor="#FFF3C8" />
				<stop offset="1" stopColor="#FFFAE5" />
			</linearGradient>
			<linearGradient
				id="paint2_linear_116_4647"
				x1="55.0466"
				y1="19.0906"
				x2="46.4767"
				y2="7.29738"
				gradientUnits="userSpaceOnUse"
			>
				<stop offset="0.00132211" stopColor="#FFE380" />
				<stop offset="0.1843" stopColor="#FFE485" />
				<stop offset="0.3901" stopColor="#FFE792" />
				<stop offset="0.6069" stopColor="#FFECA9" />
				<stop offset="0.83" stopColor="#FFF3C8" />
				<stop offset="1" stopColor="#FFFAE5" />
			</linearGradient>
			<linearGradient
				id="paint3_linear_116_4647"
				x1="52.8645"
				y1="-16.6325"
				x2="52.8645"
				y2="88.5063"
				gradientUnits="userSpaceOnUse"
			>
				<stop offset="0.00132211" stopColor="#0065FF" />
				<stop offset="0.2152" stopColor="#0568FF" />
				<stop offset="0.4558" stopColor="#1272FF" />
				<stop offset="0.7093" stopColor="#2981FF" />
				<stop offset="0.9703" stopColor="#4897FF" />
				<stop offset="1" stopColor="#4C9AFF" />
			</linearGradient>
			<linearGradient
				id="paint4_linear_116_4647"
				x1="52.7621"
				y1="-16.6325"
				x2="52.7621"
				y2="88.5062"
				gradientUnits="userSpaceOnUse"
			>
				<stop offset="0.00132211" stopColor="#0065FF" />
				<stop offset="0.2152" stopColor="#0568FF" />
				<stop offset="0.4558" stopColor="#1272FF" />
				<stop offset="0.7093" stopColor="#2981FF" />
				<stop offset="0.9703" stopColor="#4897FF" />
				<stop offset="1" stopColor="#4C9AFF" />
			</linearGradient>
			<linearGradient
				id="paint5_linear_116_4647"
				x1="52.7621"
				y1="56.6669"
				x2="52.7621"
				y2="87.466"
				gradientUnits="userSpaceOnUse"
			>
				<stop offset="0.00132211" stopColor="#0065FF" />
				<stop offset="1" stopColor="#2684FF" />
			</linearGradient>
			<linearGradient
				id="paint6_linear_116_4647"
				x1="52.7873"
				y1="-16.6326"
				x2="52.7873"
				y2="88.5062"
				gradientUnits="userSpaceOnUse"
			>
				<stop offset="0.00132211" stopColor="#0065FF" />
				<stop offset="0.2152" stopColor="#0568FF" />
				<stop offset="0.4558" stopColor="#1272FF" />
				<stop offset="0.7093" stopColor="#2981FF" />
				<stop offset="0.9703" stopColor="#4897FF" />
				<stop offset="1" stopColor="#4C9AFF" />
			</linearGradient>
			<linearGradient
				id="paint7_linear_116_4647"
				x1="52.7873"
				y1="173.956"
				x2="52.7873"
				y2="73.2456"
				gradientUnits="userSpaceOnUse"
			>
				<stop offset="0.00132211" stopColor="#0065FF" />
				<stop offset="0.2152" stopColor="#0568FF" />
				<stop offset="0.4558" stopColor="#1272FF" />
				<stop offset="0.7093" stopColor="#2981FF" />
				<stop offset="0.9703" stopColor="#4897FF" />
				<stop offset="1" stopColor="#4C9AFF" />
			</linearGradient>
			<linearGradient
				id="paint8_linear_116_4647"
				x1="83.4238"
				y1="-16.6329"
				x2="83.4238"
				y2="88.5069"
				gradientUnits="userSpaceOnUse"
			>
				<stop offset="0.00132211" stopColor="#0065FF" />
				<stop offset="0.2152" stopColor="#0568FF" />
				<stop offset="0.4558" stopColor="#1272FF" />
				<stop offset="0.7093" stopColor="#2981FF" />
				<stop offset="0.9703" stopColor="#4897FF" />
				<stop offset="1" stopColor="#4C9AFF" />
			</linearGradient>
			<linearGradient
				id="paint9_linear_116_4647"
				x1="76.6159"
				y1="22.6734"
				x2="110.018"
				y2="22.6734"
				gradientUnits="userSpaceOnUse"
			>
				<stop offset="0.0334535" stopColor="#0049B0" />
				<stop offset="0.6292" stopColor="#005ADF" />
				<stop offset="1" stopColor="#0065FF" />
			</linearGradient>
			<linearGradient
				id="paint10_linear_116_4647"
				x1="84.1725"
				y1="-16.6328"
				x2="84.1725"
				y2="88.5069"
				gradientUnits="userSpaceOnUse"
			>
				<stop offset="0.00132211" stopColor="#0065FF" />
				<stop offset="0.2152" stopColor="#0568FF" />
				<stop offset="0.4558" stopColor="#1272FF" />
				<stop offset="0.7093" stopColor="#2981FF" />
				<stop offset="0.9703" stopColor="#4897FF" />
				<stop offset="1" stopColor="#4C9AFF" />
			</linearGradient>
			<linearGradient
				id="paint11_linear_116_4647"
				x1="76.6158"
				y1="22.8981"
				x2="110.018"
				y2="22.8981"
				gradientUnits="userSpaceOnUse"
			>
				<stop offset="0.0334535" stopColor="#0049B0" />
				<stop offset="0.6292" stopColor="#005ADF" />
				<stop offset="1" stopColor="#0065FF" />
			</linearGradient>
			<linearGradient
				id="paint12_linear_116_4647"
				x1="86.5815"
				y1="-16.6324"
				x2="86.5815"
				y2="88.5061"
				gradientUnits="userSpaceOnUse"
			>
				<stop offset="0.00132211" stopColor="#0065FF" />
				<stop offset="0.2152" stopColor="#0568FF" />
				<stop offset="0.4558" stopColor="#1272FF" />
				<stop offset="0.7093" stopColor="#2981FF" />
				<stop offset="0.9703" stopColor="#4897FF" />
				<stop offset="1" stopColor="#4C9AFF" />
			</linearGradient>
			<linearGradient
				id="paint13_linear_116_4647"
				x1="76.6159"
				y1="23.3949"
				x2="110.018"
				y2="23.3949"
				gradientUnits="userSpaceOnUse"
			>
				<stop offset="0.0334535" stopColor="#0049B0" />
				<stop offset="0.6292" stopColor="#005ADF" />
				<stop offset="1" stopColor="#0065FF" />
			</linearGradient>
			<linearGradient
				id="paint14_linear_116_4647"
				x1="94.1585"
				y1="-16.6325"
				x2="94.1585"
				y2="88.5062"
				gradientUnits="userSpaceOnUse"
			>
				<stop offset="0.00132211" stopColor="#0065FF" />
				<stop offset="0.2152" stopColor="#0568FF" />
				<stop offset="0.4558" stopColor="#1272FF" />
				<stop offset="0.7093" stopColor="#2981FF" />
				<stop offset="0.9703" stopColor="#4897FF" />
				<stop offset="1" stopColor="#4C9AFF" />
			</linearGradient>
			<linearGradient
				id="paint15_linear_116_4647"
				x1="76.6157"
				y1="24.0601"
				x2="110.018"
				y2="24.0601"
				gradientUnits="userSpaceOnUse"
			>
				<stop offset="0.0334535" stopColor="#0049B0" />
				<stop offset="0.6292" stopColor="#005ADF" />
				<stop offset="1" stopColor="#0065FF" />
			</linearGradient>
			<linearGradient
				id="paint16_linear_116_4647"
				x1="89.9729"
				y1="-16.6325"
				x2="89.9729"
				y2="88.5062"
				gradientUnits="userSpaceOnUse"
			>
				<stop offset="0.00132211" stopColor="#0065FF" />
				<stop offset="0.2152" stopColor="#0568FF" />
				<stop offset="0.4558" stopColor="#1272FF" />
				<stop offset="0.7093" stopColor="#2981FF" />
				<stop offset="0.9703" stopColor="#4897FF" />
				<stop offset="1" stopColor="#4C9AFF" />
			</linearGradient>
			<linearGradient
				id="paint17_linear_116_4647"
				x1="76.6158"
				y1="23.929"
				x2="110.018"
				y2="23.929"
				gradientUnits="userSpaceOnUse"
			>
				<stop offset="0.0334535" stopColor="#0049B0" />
				<stop offset="0.6292" stopColor="#005ADF" />
				<stop offset="1" stopColor="#0065FF" />
			</linearGradient>
			<linearGradient
				id="paint18_linear_116_4647"
				x1="90.8915"
				y1="-16.6309"
				x2="90.8915"
				y2="88.5037"
				gradientUnits="userSpaceOnUse"
			>
				<stop offset="0.00132211" stopColor="#0065FF" />
				<stop offset="0.2152" stopColor="#0568FF" />
				<stop offset="0.4558" stopColor="#1272FF" />
				<stop offset="0.7093" stopColor="#2981FF" />
				<stop offset="0.9703" stopColor="#4897FF" />
				<stop offset="1" stopColor="#4C9AFF" />
			</linearGradient>
			<linearGradient
				id="paint19_linear_116_4647"
				x1="76.6157"
				y1="23.9981"
				x2="110.018"
				y2="23.9981"
				gradientUnits="userSpaceOnUse"
			>
				<stop offset="0.0334535" stopColor="#0049B0" />
				<stop offset="0.6292" stopColor="#005ADF" />
				<stop offset="1" stopColor="#0065FF" />
			</linearGradient>
			<linearGradient
				id="paint20_linear_116_4647"
				x1="89.0776"
				y1="-16.6332"
				x2="89.0776"
				y2="88.5075"
				gradientUnits="userSpaceOnUse"
			>
				<stop offset="0.00132211" stopColor="#0065FF" />
				<stop offset="0.2152" stopColor="#0568FF" />
				<stop offset="0.4558" stopColor="#1272FF" />
				<stop offset="0.7093" stopColor="#2981FF" />
				<stop offset="0.9703" stopColor="#4897FF" />
				<stop offset="1" stopColor="#4C9AFF" />
			</linearGradient>
			<linearGradient
				id="paint21_linear_116_4647"
				x1="76.6159"
				y1="23.8393"
				x2="110.017"
				y2="23.8393"
				gradientUnits="userSpaceOnUse"
			>
				<stop offset="0.0334535" stopColor="#0049B0" />
				<stop offset="0.6292" stopColor="#005ADF" />
				<stop offset="1" stopColor="#0065FF" />
			</linearGradient>
			<linearGradient
				id="paint22_linear_116_4647"
				x1="91.8126"
				y1="-16.6341"
				x2="91.8126"
				y2="88.5087"
				gradientUnits="userSpaceOnUse"
			>
				<stop offset="0.00132211" stopColor="#0065FF" />
				<stop offset="0.2152" stopColor="#0568FF" />
				<stop offset="0.4558" stopColor="#1272FF" />
				<stop offset="0.7093" stopColor="#2981FF" />
				<stop offset="0.9703" stopColor="#4897FF" />
				<stop offset="1" stopColor="#4C9AFF" />
			</linearGradient>
			<linearGradient
				id="paint23_linear_116_4647"
				x1="76.6157"
				y1="24.0449"
				x2="110.018"
				y2="24.0449"
				gradientUnits="userSpaceOnUse"
			>
				<stop offset="0.0334535" stopColor="#0049B0" />
				<stop offset="0.6292" stopColor="#005ADF" />
				<stop offset="1" stopColor="#0065FF" />
			</linearGradient>
			<linearGradient
				id="paint24_linear_116_4647"
				x1="81.4449"
				y1="-16.6325"
				x2="81.4449"
				y2="88.5062"
				gradientUnits="userSpaceOnUse"
			>
				<stop offset="0.00132211" stopColor="#0065FF" />
				<stop offset="0.2152" stopColor="#0568FF" />
				<stop offset="0.4558" stopColor="#1272FF" />
				<stop offset="0.7093" stopColor="#2981FF" />
				<stop offset="0.9703" stopColor="#4897FF" />
				<stop offset="1" stopColor="#4C9AFF" />
			</linearGradient>
			<linearGradient
				id="paint25_linear_116_4647"
				x1="76.6158"
				y1="21.9349"
				x2="110.018"
				y2="21.9349"
				gradientUnits="userSpaceOnUse"
			>
				<stop offset="0.0334535" stopColor="#0049B0" />
				<stop offset="0.6292" stopColor="#005ADF" />
				<stop offset="1" stopColor="#0065FF" />
			</linearGradient>
			<linearGradient
				id="paint26_linear_116_4647"
				x1="82.7277"
				y1="-16.6329"
				x2="82.7277"
				y2="88.5068"
				gradientUnits="userSpaceOnUse"
			>
				<stop offset="0.00132211" stopColor="#0065FF" />
				<stop offset="0.2152" stopColor="#0568FF" />
				<stop offset="0.4558" stopColor="#1272FF" />
				<stop offset="0.7093" stopColor="#2981FF" />
				<stop offset="0.9703" stopColor="#4897FF" />
				<stop offset="1" stopColor="#4C9AFF" />
			</linearGradient>
			<linearGradient
				id="paint27_linear_116_4647"
				x1="76.6158"
				y1="22.4396"
				x2="110.018"
				y2="22.4396"
				gradientUnits="userSpaceOnUse"
			>
				<stop offset="0.0334535" stopColor="#0049B0" />
				<stop offset="0.6292" stopColor="#005ADF" />
				<stop offset="1" stopColor="#0065FF" />
			</linearGradient>
			<linearGradient
				id="paint28_linear_116_4647"
				x1="80.8455"
				y1="-16.6325"
				x2="80.8455"
				y2="88.5062"
				gradientUnits="userSpaceOnUse"
			>
				<stop offset="0.00132211" stopColor="#0065FF" />
				<stop offset="0.2152" stopColor="#0568FF" />
				<stop offset="0.4558" stopColor="#1272FF" />
				<stop offset="0.7093" stopColor="#2981FF" />
				<stop offset="0.9703" stopColor="#4897FF" />
				<stop offset="1" stopColor="#4C9AFF" />
			</linearGradient>
			<linearGradient
				id="paint29_linear_116_4647"
				x1="76.6159"
				y1="21.6592"
				x2="110.018"
				y2="21.6592"
				gradientUnits="userSpaceOnUse"
			>
				<stop offset="0.0334535" stopColor="#0049B0" />
				<stop offset="0.6292" stopColor="#005ADF" />
				<stop offset="1" stopColor="#0065FF" />
			</linearGradient>
			<linearGradient
				id="paint30_linear_116_4647"
				x1="80.252"
				y1="-16.6325"
				x2="80.252"
				y2="88.5062"
				gradientUnits="userSpaceOnUse"
			>
				<stop offset="0.00132211" stopColor="#0065FF" />
				<stop offset="0.2152" stopColor="#0568FF" />
				<stop offset="0.4558" stopColor="#1272FF" />
				<stop offset="0.7093" stopColor="#2981FF" />
				<stop offset="0.9703" stopColor="#4897FF" />
				<stop offset="1" stopColor="#4C9AFF" />
			</linearGradient>
			<linearGradient
				id="paint31_linear_116_4647"
				x1="76.6158"
				y1="21.355"
				x2="110.018"
				y2="21.355"
				gradientUnits="userSpaceOnUse"
			>
				<stop offset="0.0334535" stopColor="#0049B0" />
				<stop offset="0.6292" stopColor="#005ADF" />
				<stop offset="1" stopColor="#0065FF" />
			</linearGradient>
			<linearGradient
				id="paint32_linear_116_4647"
				x1="82.0708"
				y1="-16.6329"
				x2="82.0708"
				y2="88.5068"
				gradientUnits="userSpaceOnUse"
			>
				<stop offset="0.00132211" stopColor="#0065FF" />
				<stop offset="0.2152" stopColor="#0568FF" />
				<stop offset="0.4558" stopColor="#1272FF" />
				<stop offset="0.7093" stopColor="#2981FF" />
				<stop offset="0.9703" stopColor="#4897FF" />
				<stop offset="1" stopColor="#4C9AFF" />
			</linearGradient>
			<linearGradient
				id="paint33_linear_116_4647"
				x1="76.6157"
				y1="22.1942"
				x2="110.018"
				y2="22.1942"
				gradientUnits="userSpaceOnUse"
			>
				<stop offset="0.0334535" stopColor="#0049B0" />
				<stop offset="0.6292" stopColor="#005ADF" />
				<stop offset="1" stopColor="#0065FF" />
			</linearGradient>
			<linearGradient
				id="paint34_linear_116_4647"
				x1="90.5222"
				y1="-16.6325"
				x2="90.5222"
				y2="88.5062"
				gradientUnits="userSpaceOnUse"
			>
				<stop offset="0.00132211" stopColor="#0065FF" />
				<stop offset="0.2152" stopColor="#0568FF" />
				<stop offset="0.4558" stopColor="#1272FF" />
				<stop offset="0.7093" stopColor="#2981FF" />
				<stop offset="0.9703" stopColor="#4897FF" />
				<stop offset="1" stopColor="#4C9AFF" />
			</linearGradient>
			<linearGradient
				id="paint35_linear_116_4647"
				x1="76.6158"
				y1="16.8491"
				x2="110.018"
				y2="16.8491"
				gradientUnits="userSpaceOnUse"
			>
				<stop offset="0.0334535" stopColor="#0049B0" />
				<stop offset="0.6292" stopColor="#005ADF" />
				<stop offset="1" stopColor="#0065FF" />
			</linearGradient>
			<clipPath id="clip0_116_4647">
				<rect width="120" height="115.256" fill="white" />
			</clipPath>
		</defs>
	</svg>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MessageContainer = styled.div({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	textAlign: 'center',
	margin: `${token('space.600', '48px')} auto ${token('space.600', '48px')} auto`,
	gap: token('space.200', '16'),
	width: '100%',
	height: '100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MessageText = styled.div({
	fontWeight: 600,
	margin: `${token('space.300', '24px')} 0 ${token('space.075', '6px')}`,
});

export const TrashMessage: FC = () => (
	<MessageContainer>
		<TrashIllustration />
		<MessageText>
			<FormattedMessage {...i18n.trashMessage} />
		</MessageText>
		<LinkButton href={ADMIN_TRASHED_SPACES.pattern}>
			<FormattedMessage {...i18n.goToSettings} />
		</LinkButton>
	</MessageContainer>
);
